import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import useAxiosAuth from '../hooks/useAxiosAuth';
import baseURL from '../utils/baseURL';

// TEAM CHAT (Phase 4, decisions log #14) — the staff side of the generalized
// chat-threads backbone (/api/chat-threads, built in Phase 3). This slice only
// handles TEAM threads; the legacy doctor<->patient chat keeps its own socket
// slice untouched.
//
// Live updates are POLLING with an `after` cursor — the server's socket layer
// deliberately never carries message content (soft handshake gate), it only
// rings a doorbell. Polling every few seconds against ?after=<lastId> costs one
// cheap indexed query and returns [] almost always.
//
// UNREAD COUNTS AND PREVIEWS COME FROM THE SERVER (owner batch, 16 Sep 2026).
// GET /chat-threads now answers with `unreadCount`, `lastMessage { id, body,
// senderId, senderName, createdAt }` and `lastReadMessageId` per thread, and
// POST /chat-threads/:id/read moves the caller's marker
// (chat_thread_participants.lastReadMessageId). Nothing about a badge lives in
// this browser any more — the same person sees the same count on their phone.
//
// THREE KINDS OF FETCH, ONE MERGE (P32). An uncursored fetch opens a thread
// (the server answers with its LATEST page); `after` is the poll for what is
// new; `before` is scrolling up for an older page. Every answer is MERGED into
// the list by id and kept in id order — nothing ever replaces it — so a poll
// can never wipe a message this client just sent or an older page it loaded.

const errOf = (error, fallback) =>
  error.response?.data?.message || error.message || fallback;

export const fetchThreads = createAsyncThunk(
  'chatThreads/fetchThreads',
  async (_, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const { data } = await axiosInstance.get(`${baseURL}/chat-threads`);
      return data;
    } catch (error) {
      return rejectWithValue(errOf(error, 'Failed to load conversations'));
    }
  }
);

export const createThread = createAsyncThunk(
  'chatThreads/createThread',
  async ({ title, participants }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const { data } = await axiosInstance.post(`${baseURL}/chat-threads`, {
        type: 'team', title, participants,
      });
      return data;
    } catch (error) {
      return rejectWithValue(errOf(error, 'Failed to start the conversation'));
    }
  }
);

export const fetchMessages = createAsyncThunk(
  'chatThreads/fetchMessages',
  async ({ threadId, after, before }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const qs = after ? `?after=${after}` : before ? `?before=${before}` : '';
      const { data } = await axiosInstance.get(`${baseURL}/chat-threads/${threadId}/messages${qs}`);
      return {
        threadId,
        after: after || null,
        before: before || null,
        messages: data.messages || [],
        // Whether anything OLDER than this page exists. The server names it
        // `hasOlder` (on the opening read and on `before` pages — never on an
        // `after` poll); `hasMore` is still read so an older server keeps
        // working. Reading only `hasMore` meant the flag was never seen, so
        // scrolling up always asked once more past the top (P32). When neither
        // is sent, an empty older page is what marks the top.
        hasOlder: typeof data.hasOlder === 'boolean'
          ? data.hasOlder
          : (typeof data.hasMore === 'boolean' ? data.hasMore : null),
      };
    } catch (error) {
      // status rides along so the page can DESELECT a thread that has become
      // unreachable (revoked permission / feature off) instead of toasting the
      // same 404 on every poll.
      return rejectWithValue({
        message: errOf(error, 'Failed to load messages'),
        status: error.response?.status || null,
        threadId,
      });
    }
  }
);

export const postMessage = createAsyncThunk(
  'chatThreads/postMessage',
  async ({ threadId, body }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const { data } = await axiosInstance.post(`${baseURL}/chat-threads/${threadId}/messages`, { body });
      return { threadId, message: data.message };
    } catch (error) {
      return rejectWithValue(errOf(error, 'Failed to send'));
    }
  }
);

export const markThreadRead = createAsyncThunk(
  'chatThreads/markThreadRead',
  async ({ threadId, messageId }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const { data } = await axiosInstance.post(
        `${baseURL}/chat-threads/${threadId}/read`,
        messageId ? { messageId } : {},
      );
      return { threadId, lastReadMessageId: data.lastReadMessageId ?? null, unreadCount: data.unreadCount ?? 0 };
    } catch (error) {
      // A marker that did not save is not worth a toast: the count is a
      // courtesy, the next list refresh tells the truth again, and the reader
      // is looking at the messages either way.
      return rejectWithValue(errOf(error, 'Failed to mark as read'));
    }
  }
);

const chatThreadSlice = createSlice({
  name: 'chatThreads',
  initialState: {
    threads: [],
    threadsLoading: false,
    // threadId -> ordered message rows. Appended by cursor polls, so a poll
    // returning [] costs no re-render churn.
    messagesByThread: {},
    messagesLoading: false,
    // An older page (`before`) is on its way — the thread shows a small
    // "loading earlier" row instead of the full skeleton.
    olderLoading: false,
    // threadId -> true once the top of the thread has been reached.
    noOlderByThread: {},
    sending: false,
    error: null,
    // threadId that 404'd on open — the page deselects it once and clears this.
    goneThreadId: null,
  },
  reducers: {
    clearChatError(state) { state.error = null; },
    clearGoneThread(state) { state.goneThreadId = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchThreads.pending, (state) => { state.threadsLoading = true; })
      .addCase(fetchThreads.fulfilled, (state, action) => {
        state.threadsLoading = false;
        state.threads = (action.payload.threads || []).filter((t) => t.type === 'team');
      })
      .addCase(fetchThreads.rejected, (state, action) => {
        state.threadsLoading = false;
        state.error = action.payload;
      })
      .addCase(createThread.fulfilled, (state, action) => {
        const t = action.payload.thread;
        if (t) state.threads = [{ ...t, participants: [] }, ...state.threads];
      })
      .addCase(createThread.rejected, (state, action) => { state.error = action.payload; })
      .addCase(fetchMessages.pending, (state, action) => {
        const { after, before } = action.meta.arg;
        if (before) state.olderLoading = true;
        else if (!after) state.messagesLoading = true;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        const { threadId, after, before, messages, hasOlder } = action.payload;
        if (before) state.olderLoading = false;
        else if (!after) state.messagesLoading = false;
        // The top of the thread: the server saying nothing older exists (on
        // the opening page or an older one), or an older page that came back
        // empty from a server that does not say. A poll (`after`) never
        // decides it — it reads forwards.
        if ((before || !after) && (hasOlder === false || (before && hasOlder !== true && !messages.length))) {
          state.noOlderByThread[threadId] = true;
        }
        if (!messages.length) return;
        const existing = state.messagesByThread[threadId] || [];
        const known = new Set(existing.map((m) => String(m.id)));
        const merged = [...existing, ...messages.filter((m) => !known.has(String(m.id)))];
        merged.sort((a, b) => Number(a.id) - Number(b.id));
        state.messagesByThread[threadId] = merged;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        if (action.meta.arg.before) state.olderLoading = false;
        else if (!action.meta.arg.after) state.messagesLoading = false;
        const p = action.payload;
        if (p && typeof p === 'object' && p.status === 404) {
          // The thread is gone for this user — drop it from the list; the page
          // deselects via goneThreadId rather than an error toast loop.
          state.goneThreadId = p.threadId;
          state.threads = state.threads.filter((t) => t.threadId !== p.threadId);
        } else {
          state.error = typeof p === 'object' ? p?.message : p;
        }
      })
      .addCase(postMessage.pending, (state) => { state.sending = true; })
      .addCase(postMessage.fulfilled, (state, action) => {
        state.sending = false;
        const { threadId, message } = action.payload;
        if (!message) return;
        const list = state.messagesByThread[threadId] || [];
        if (!list.some((m) => String(m.id) === String(message.id))) {
          state.messagesByThread[threadId] = [...list, message];
        }
        const t = state.threads.find((x) => x.threadId === threadId);
        if (t) {
          t.lastMessageAt = message.created_at || new Date().toISOString();
          // The list row follows what was just said without waiting for the
          // 30s refresh. The server also advances the sender's own marker when
          // it accepts the message, which is why lastReadMessageId moves too.
          t.lastMessage = {
            id: message.id,
            body: message.body,
            senderId: message.senderId,
            senderName: null,
            createdAt: message.created_at || new Date().toISOString(),
          };
          t.lastReadMessageId = message.id;
          t.unreadCount = 0;
        }
      })
      .addCase(postMessage.rejected, (state, action) => {
        state.sending = false;
        state.error = action.payload;
      })
      // Optimistic on the way OUT (the badge clears the moment the thread is
      // opened) and corrected on the way back with what the server actually
      // stored. A failure leaves the row as the last list refresh had it.
      .addCase(markThreadRead.pending, (state, action) => {
        const t = state.threads.find((x) => x.threadId === action.meta.arg.threadId);
        if (t) t.unreadCount = 0;
      })
      .addCase(markThreadRead.fulfilled, (state, action) => {
        const { threadId, lastReadMessageId, unreadCount } = action.payload;
        const t = state.threads.find((x) => x.threadId === threadId);
        if (!t) return;
        t.lastReadMessageId = lastReadMessageId;
        t.unreadCount = unreadCount;
      });
  },
});

export const { clearChatError, clearGoneThread } = chatThreadSlice.actions;
export default chatThreadSlice.reducer;
